import type { Workspace } from "./api";
import { Eyebrow, Notice, Panel, STATUS_META, fmtDate } from "./shared";

export default function ReportsPanel({ data, onOpen }: { data: Workspace; onOpen: (id: number) => void }) {
  const s = data.stats;
  const tiles: [string, number, string][] = [
    ["Sent", s.sent, "Handed to the provider"], ["Delivered", s.delivered, "Confirmed by provider"],
    ["Bounced", s.bounced, "Now suppressed"], ["Complaints", s.complained, "Now suppressed"],
    ["Pending", s.pending, "Awaiting delivery result"], ["Unknown", s.unknown, "No provider event yet"],
  ];
  const byStatus = Object.keys(STATUS_META).map((k) => [k, data.messages.filter((m) => m.status === k).length] as const);
  const bySource = data.sources.map((src) => {
    const ms = data.messages.filter((m) => m.source === src.id);
    return { ...src, count: ms.length, sent: ms.reduce((a, m) => a + m.sent, 0), excluded: ms.reduce((a, m) => a + m.excluded, 0), failed: ms.reduce((a, m) => a + m.failed, 0) };
  }).filter((x) => x.count > 0);
  const activity = [...data.messages].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 15);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(([k, v, hint]) => (
          <Panel key={k} className="p-4"><Eyebrow>{k}</Eyebrow><p className="mt-1 font-mono text-2xl" data-testid={`stat-${k.toLowerCase()}`}>{v.toLocaleString("en-GB")}</p><p className="text-[11px] text-muted-foreground">{hint}</p></Panel>
        ))}
      </div>
      <Notice>Open tracking is unreliable (mail apps preload or block images), so we show {s.opened.toLocaleString("en-GB")} recorded opens as a raw count only and do not calculate an open rate. Figures cover the last 200 workspace messages.</Notice>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel className="p-5">
          <Eyebrow>Messages by status</Eyebrow>
          <ul className="mt-3 space-y-2">
            {byStatus.map(([k, n]) => {
              const max = Math.max(1, ...byStatus.map((x) => x[1]));
              return <li key={k} className="grid grid-cols-[minmax(0,110px)_1fr_40px] items-center gap-3 text-sm"><span>{STATUS_META[k as keyof typeof STATUS_META].label}</span><span className="h-2 rounded-full bg-muted overflow-hidden"><span className="block h-full bg-[#FF5C00] origin-left" style={{ transform: `scaleX(${n / max})` }} /></span><span className="font-mono text-right">{n}</span></li>;
            })}
          </ul>
        </Panel>
        <Panel className="p-5">
          <Eyebrow>By source</Eyebrow>
          {bySource.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No messages yet.</p> : (
            <div className="mt-3 overflow-x-auto"><table className="w-full text-sm min-w-[340px]"><thead className="text-[11px] text-muted-foreground"><tr><th className="text-left font-medium pb-2">Source</th><th className="text-right font-medium">Msgs</th><th className="text-right font-medium">Sent</th><th className="text-right font-medium">Excl.</th><th className="text-right font-medium">Failed</th></tr></thead>
              <tbody className="divide-y divide-border">{bySource.map((x) => <tr key={x.id}><td className="py-2">{x.label}{x.essential && <span className="ml-1 text-[10px] text-muted-foreground">essential</span>}</td><td className="text-right font-mono">{x.count}</td><td className="text-right font-mono">{x.sent}</td><td className="text-right font-mono">{x.excluded}</td><td className="text-right font-mono">{x.failed}</td></tr>)}</tbody></table></div>
          )}
        </Panel>
      </div>
      <Panel className="p-5">
        <Eyebrow>Recent activity</Eyebrow>
        {activity.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No activity yet.</p> : (
          <ul className="mt-3 divide-y divide-border">{activity.map((m) => (
            <li key={m.id}><button data-testid={`row-activity-${m.id}`} onClick={() => onOpen(m.id)} className="w-full text-left py-2.5 flex flex-wrap items-baseline justify-between gap-2 hover:text-[#C24600] dark:hover:text-[#FF8A47]"><span className="text-sm">{m.subject} <span className="text-muted-foreground">· {STATUS_META[m.status]?.label || m.status}</span></span><span className="text-xs text-muted-foreground">{fmtDate(m.updated_at)}</span></button></li>
          ))}</ul>
        )}
      </Panel>
    </div>
  );
}
